/**
 * Track XB Wishlist "Add to Favorites" as Klaviyo metric "Added to Favorites".
 *
 * Enrichment sources (in order):
 * 1. Shopify Storefront GraphQL by product ID (XB wishlist token)
 * 2. /products/{handle}.js
 * 3. DOM / pending click attrs / page Liquid context
 */
(function () {
  var EVENT_NAME = 'Added to Favorites';
  var GRAPHQL_URL = '/api/2026-04/graphql.json';
  var TOKEN_URL = '/tools/xb-wishlist/storefront-access-token';
  var ctx = window.__klaviyoFavorites || {};
  var pendingByProductId = {};
  var recentKeys = {};
  var storefrontTokenPromise = null;
  var DEDUPE_MS = 5000;

  function ensureKlaviyo() {
    if (!window.klaviyo) {
      window._klOnsite = window._klOnsite || [];
      try {
        window.klaviyo = new Proxy(
          {},
          {
            get: function (_obj, name) {
              if (name === 'push') {
                return function () {
                  window._klOnsite.push.apply(window._klOnsite, arguments);
                };
              }
              return function () {
                var args = arguments;
                var last = args[args.length - 1];
                var promise = new Promise(function (resolve) {
                  var payload = [name].concat([].slice.call(args));
                  if (typeof last === 'function') {
                    window._klOnsite.push(
                      payload.slice(0, -1).concat([
                        function (result) {
                          last(result);
                          resolve(result);
                        }
                      ])
                    );
                  } else {
                    window._klOnsite.push(
                      payload.concat([
                        function (result) {
                          resolve(result);
                        }
                      ])
                    );
                  }
                });
                return promise;
              };
            }
          }
        );
      } catch (e) {
        window.klaviyo = window.klaviyo || [];
        window.klaviyo.push = function () {
          window._klOnsite.push.apply(window._klOnsite, arguments);
        };
      }
    }
  }

  function getCurrency() {
    return (
      (window.Shopify && Shopify.currency && Shopify.currency.active) ||
      ctx.currency ||
      ''
    );
  }

  function getRoot() {
    var root = ctx.root || '/';
    if (root.slice(-1) !== '/') root += '/';
    return root;
  }

  function centsToPrice(cents) {
    var n = Number(cents);
    if (!isFinite(n)) return null;
    return Math.round(n) / 100;
  }

  function normalizePrice(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number') {
      return value > 9999 ? centsToPrice(value) : value;
    }
    var cleaned = String(value).replace(/[^\d.,-]/g, '').replace(',', '.');
    var n = parseFloat(cleaned);
    return isFinite(n) ? n : null;
  }

  function absoluteUrl(path) {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    if (path.indexOf('//') === 0) return window.location.protocol + path;
    return window.location.origin + (path.charAt(0) === '/' ? path : '/' + path);
  }

  function productUrlFromHandle(handle) {
    if (!handle) return '';
    return absoluteUrl(getRoot() + 'products/' + handle);
  }

  function handleFromHref(href) {
    if (!href) return '';
    var match = String(href).match(/\/products\/([^\/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function gidToId(gid) {
    if (!gid) return '';
    var parts = String(gid).split('/');
    return parts[parts.length - 1] || '';
  }

  function attr(node, names) {
    if (!node || !node.getAttribute) return '';
    for (var i = 0; i < names.length; i++) {
      var value = node.getAttribute(names[i]);
      if (value != null && String(value).trim() !== '') return String(value).trim();
    }
    return '';
  }

  function rememberPending(data) {
    if (!data || !data.productId) return;
    pendingByProductId[String(data.productId)] = Object.assign(
      { ts: Date.now() },
      data
    );
  }

  function peekPending(productId) {
    var key = String(productId || '');
    var data = pendingByProductId[key];
    if (!data) return null;
    if (Date.now() - data.ts > 20000) {
      delete pendingByProductId[key];
      return null;
    }
    return data;
  }

  function readAttrs(node) {
    if (!node || !node.getAttribute) return null;

    var productId = attr(node, ['xb-product-id', 'data-product-id', 'product-id']);
    var handle = attr(node, ['xb-handle', 'xb-product-handle', 'data-product-handle']);
    var title = attr(node, ['xb-title', 'xb-product-title', 'data-product-title']);
    var variantId = attr(node, [
      'xb-variant-id',
      'xb-product-variant',
      'data-variant-id'
    ]);
    var price = attr(node, ['xb-price', 'data-price']);

    if (!productId && node.productId != null) productId = String(node.productId);
    if (!handle && node.handle) handle = String(node.handle);
    if (!title && node.title) title = String(node.title);
    if (!variantId && node.variantId != null) variantId = String(node.variantId);

    if (!productId && !handle) return null;

    var data = {
      productId: productId ? String(productId) : '',
      productHandle: handle || '',
      productTitle: title || '',
      variantId: variantId ? String(variantId) : ''
    };
    if (price !== '') data.price = normalizePrice(price);
    return data;
  }

  function findHandleNear(el) {
    if (!el || !el.closest) return '';
    var card = el.closest(
      '.product-item, .product-card, .grid__item, li, article, [data-product-id]'
    );
    if (!card) return '';
    var link =
      card.querySelector('a.product-item__title[href*="/products/"]') ||
      card.querySelector('a.product-item__image-wrapper[href*="/products/"]') ||
      card.querySelector('a[href*="/products/"]');
    return handleFromHref(link && (link.getAttribute('href') || link.href));
  }

  function queryDeepAll(selector, root) {
    root = root || document;
    var found = [];
    try {
      found = found.concat(Array.prototype.slice.call(root.querySelectorAll(selector)));
    } catch (e) {}
    var all = root.querySelectorAll ? root.querySelectorAll('*') : [];
    for (var i = 0; i < all.length; i++) {
      if (all[i].shadowRoot) {
        found = found.concat(queryDeepAll(selector, all[i].shadowRoot));
      }
    }
    return found;
  }

  function mergeData(a, b) {
    a = a || {};
    b = b || {};
    return {
      productId: a.productId || b.productId || '',
      productHandle: a.productHandle || b.productHandle || '',
      productTitle: a.productTitle || b.productTitle || '',
      variantId: a.variantId || b.variantId || '',
      price: a.price != null ? a.price : b.price
    };
  }

  function extractFromPath(path, fallbackEl) {
    var data = null;
    var nodes = path && path.length ? path : fallbackEl ? [fallbackEl] : [];

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (!node) continue;
      var tag = (node.tagName || '').toLowerCase();
      if (
        tag === 'xb-wishlist-button' ||
        tag === 'xb-wishlist-button-auto' ||
        (node.classList && node.classList.contains('xb-wishlist__add'))
      ) {
        data = mergeData(data, readAttrs(node));
      } else {
        var maybe = readAttrs(node);
        if (maybe) data = mergeData(data, maybe);
      }
      if (node.getRootNode) {
        var root = node.getRootNode();
        if (root && root.host) data = mergeData(data, readAttrs(root.host));
      }
    }

    if (data && !data.productHandle && fallbackEl) {
      data.productHandle = findHandleNear(fallbackEl);
    }

    return data && (data.productId || data.productHandle) ? data : null;
  }

  function findDomProductData(productId) {
    if (!productId) return null;
    var id = String(productId);
    var selectors = [
      'xb-wishlist-button[xb-product-id="' + id + '"]',
      '[xb-product-id="' + id + '"]'
    ];
    for (var s = 0; s < selectors.length; s++) {
      var matches = queryDeepAll(selectors[s]);
      for (var i = 0; i < matches.length; i++) {
        var data = mergeData(readAttrs(matches[i]), null);
        if (matches[i].getRootNode && matches[i].getRootNode().host) {
          data = mergeData(data, readAttrs(matches[i].getRootNode().host));
        }
        if (!data.productHandle) data.productHandle = findHandleNear(matches[i]);
        if (data.productId || data.productHandle) return data;
      }
    }
    if (ctx.product && String(ctx.product.id) === id) {
      return {
        productId: id,
        productHandle: ctx.product.handle,
        productTitle: ctx.product.title,
        variantId: ctx.product.variantId ? String(ctx.product.variantId) : ''
      };
    }
    return null;
  }

  function readWishlistLocalStorage(productId, variantId) {
    try {
      var raw = window.localStorage.getItem('xb_wishlist_data');
      if (!raw) return null;
      var items = JSON.parse(raw);
      if (!Array.isArray(items)) return null;
      var id = String(productId || '');
      var vid = variantId ? String(variantId) : '';
      for (var i = 0; i < items.length; i++) {
        var item = items[i] || {};
        if (String(item.productId) !== id) continue;
        if (vid && item.variantId && String(item.variantId) !== vid) continue;
        return {
          productId: String(item.productId || id),
          productHandle: item.handle || '',
          productTitle: item.title || '',
          variantId: item.variantId ? String(item.variantId) : vid,
          price: item.price != null ? normalizePrice(item.price) : undefined
        };
      }
    } catch (e) {}
    return null;
  }

  function getStorefrontToken() {
    if (
      window.xb_wishlist &&
      window.xb_wishlist.settings &&
      window.xb_wishlist.settings.storefrontAccessToken
    ) {
      return Promise.resolve(window.xb_wishlist.settings.storefrontAccessToken);
    }
    if (storefrontTokenPromise) return storefrontTokenPromise;

    storefrontTokenPromise = fetch(TOKEN_URL, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('token fetch failed');
        return res.json();
      })
      .then(function (json) {
        return json && json.storefrontAccessToken
          ? json.storefrontAccessToken
          : '';
      })
      .catch(function () {
        storefrontTokenPromise = null;
        return '';
      });

    return storefrontTokenPromise;
  }

  function fetchProductByIdGraphql(productId, variantId) {
    if (!productId) return Promise.resolve(null);

    return getStorefrontToken().then(function (token) {
      if (!token) return null;

      var locale = (window.Shopify && Shopify.locale) || 'en';
      var country = (window.Shopify && Shopify.country) || 'BG';
      var language = String(locale).toUpperCase().replace('-', '_');
      var query =
        'query @inContext(country: ' +
        country +
        ', language: ' +
        language +
        ') {\n' +
        '  product(id: "gid://shopify/Product/' +
        productId +
        '") {\n' +
        '    id\n' +
        '    title\n' +
        '    handle\n' +
        '    vendor\n' +
        '    productType\n' +
        '    tags\n' +
        '    featuredImage { url }\n' +
        '    media(first: 1) { nodes { previewImage { url } } }\n' +
        '    collections(first: 10) { nodes { title handle } }\n' +
        '    variants(first: 50) {\n' +
        '      nodes {\n' +
        '        id\n' +
        '        title\n' +
        '        sku\n' +
        '        price { amount currencyCode }\n' +
        '        image { url }\n' +
        '      }\n' +
        '    }\n' +
        '  }\n' +
        '}';

      return fetch(GRAPHQL_URL, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({ query: query })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('graphql failed');
          return res.json();
        })
        .then(function (json) {
          var product = json && json.data && json.data.product;
          if (!product) return null;

          var variants = (product.variants && product.variants.nodes) || [];
          var selected = null;
          var want = variantId ? String(variantId) : '';
          for (var i = 0; i < variants.length; i++) {
            if (gidToId(variants[i].id) === want) {
              selected = variants[i];
              break;
            }
          }
          if (!selected) selected = variants[0] || null;

          var image =
            (selected && selected.image && selected.image.url) ||
            (product.featuredImage && product.featuredImage.url) ||
            (product.media &&
              product.media.nodes &&
              product.media.nodes[0] &&
              product.media.nodes[0].previewImage &&
              product.media.nodes[0].previewImage.url) ||
            '';

          var collections = ((product.collections && product.collections.nodes) || [])
            .map(function (c) {
              return c && c.title;
            })
            .filter(Boolean);

          return {
            id: gidToId(product.id),
            title: product.title || '',
            handle: product.handle || '',
            vendor: product.vendor || '',
            type: product.productType || '',
            tags: product.tags || [],
            image: image,
            collections: collections,
            price:
              selected && selected.price
                ? normalizePrice(selected.price.amount)
                : null,
            currency:
              selected && selected.price
                ? selected.price.currencyCode
                : getCurrency(),
            variantId: selected ? gidToId(selected.id) : want,
            variantTitle: selected ? selected.title || '' : '',
            sku: selected ? selected.sku || '' : ''
          };
        })
        .catch(function () {
          return null;
        });
    });
  }

  function fetchProductByHandle(handle) {
    if (!handle) return Promise.resolve(null);
    var url = getRoot() + 'products/' + encodeURIComponent(handle) + '.js';
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('product fetch failed');
        return res.json();
      })
      .then(function (product) {
        if (!product) return null;
        var variants = product.variants || [];
        var selected = variants[0] || null;
        return {
          id: String(product.id || ''),
          title: product.title || '',
          handle: product.handle || '',
          vendor: product.vendor || '',
          type: product.type || '',
          tags: product.tags || [],
          image: product.featured_image
            ? absoluteUrl(product.featured_image)
            : product.images && product.images[0]
              ? absoluteUrl(product.images[0])
              : '',
          collections: [],
          price: selected ? centsToPrice(selected.price) : null,
          currency: getCurrency(),
          variantId: selected ? String(selected.id) : '',
          variantTitle: selected ? selected.title || '' : '',
          sku: selected ? selected.sku || '' : '',
          _variants: variants
        };
      })
      .catch(function () {
        return null;
      });
  }

  function resolveBase(raw) {
    var base = Object.assign({}, raw || {});
    var pending = peekPending(base.productId);
    if (pending) base = mergeData(base, pending);

    if ((!base.productHandle || !base.productTitle) && base.productId) {
      base = mergeData(base, findDomProductData(base.productId));
    }

    if ((!base.productHandle || !base.productTitle) && base.productId) {
      base = mergeData(
        base,
        readWishlistLocalStorage(base.productId, base.variantId)
      );
    }

    if (
      ctx.product &&
      base.productId &&
      String(ctx.product.id) === String(base.productId)
    ) {
      base.productHandle = base.productHandle || ctx.product.handle;
      base.productTitle = base.productTitle || ctx.product.title;
      base.variantId =
        base.variantId ||
        (ctx.product.variantId ? String(ctx.product.variantId) : '');
    }

    return base;
  }

  function enrichProduct(base) {
    return fetchProductByIdGraphql(base.productId, base.variantId).then(
      function (product) {
        if (product) return product;
        return fetchProductByHandle(base.productHandle).then(function (byHandle) {
          if (!byHandle) return null;
          // Prefer requested variant when using product.js
          if (base.variantId && byHandle._variants) {
            for (var i = 0; i < byHandle._variants.length; i++) {
              if (String(byHandle._variants[i].id) === String(base.variantId)) {
                byHandle.variantId = String(byHandle._variants[i].id);
                byHandle.variantTitle = byHandle._variants[i].title || '';
                byHandle.price = centsToPrice(byHandle._variants[i].price);
                byHandle.sku = byHandle._variants[i].sku || '';
                break;
              }
            }
          }
          delete byHandle._variants;
          return byHandle;
        });
      }
    );
  }

  function buildPayload(base, product) {
    var pageProduct = ctx.product;
    var samePageProduct =
      pageProduct &&
      base.productId &&
      String(pageProduct.id) === String(base.productId);

    var productId =
      (product && product.id) ||
      base.productId ||
      (samePageProduct && pageProduct.id) ||
      '';
    var title =
      (product && product.title) ||
      base.productTitle ||
      (samePageProduct && pageProduct.title) ||
      '';
    var handle =
      (product && product.handle) ||
      base.productHandle ||
      (samePageProduct && pageProduct.handle) ||
      '';
    var url =
      (samePageProduct && pageProduct.url) || productUrlFromHandle(handle);
    var image =
      (product && product.image) ||
      (samePageProduct && pageProduct.image) ||
      '';
    var price = null;
    if (product && product.price != null) {
      price = product.price;
    } else if (base.price != null) {
      price = normalizePrice(base.price);
    } else if (samePageProduct && pageProduct.priceCents != null) {
      price = centsToPrice(pageProduct.priceCents);
    }
    var currency = (product && product.currency) || getCurrency();
    var brand =
      (product && product.vendor) ||
      (samePageProduct && pageProduct.brand) ||
      '';
    var category =
      (product && product.type) ||
      (samePageProduct && pageProduct.category) ||
      '';
    var collection =
      (ctx.collection && ctx.collection.title) ||
      (product && product.collections && product.collections[0]) ||
      '';
    var variantTitle = '';
    if (product && product.variantTitle && product.variantTitle !== 'Default Title') {
      variantTitle = product.variantTitle;
    } else if (
      samePageProduct &&
      pageProduct.variant &&
      pageProduct.variant !== 'Default Title'
    ) {
      variantTitle = pageProduct.variant;
    }

    var payload = {
      'Customer Email': ctx.email || undefined,
      'Product ID': productId ? String(productId) : undefined,
      'Product Name': title || undefined,
      'Product URL': url || undefined,
      'Product Image': image || undefined,
      'Product Price': price,
      $value: price,
      Currency: currency || undefined,
      Brand: brand || undefined,
      Category: category || undefined,
      Collection: collection || undefined,
      Variant: variantTitle || undefined
    };

    Object.keys(payload).forEach(function (key) {
      if (payload[key] === undefined || payload[key] === null || payload[key] === '') {
        delete payload[key];
      }
    });

    return payload;
  }

  function alreadyTracked(productId, variantId) {
    var key = String(productId) + ':' + String(variantId || '');
    var now = Date.now();
    if (recentKeys[key] && now - recentKeys[key] < DEDUPE_MS) return true;
    recentKeys[key] = now;
    return false;
  }

  function sendToKlaviyo(payload) {
    ensureKlaviyo();

    var track = function () {
      if (typeof window.klaviyo.track === 'function') {
        window.klaviyo.track(EVENT_NAME, payload);
      } else {
        window.klaviyo.push(['track', EVENT_NAME, payload]);
      }
    };

    if (ctx.email) {
      if (typeof window.klaviyo.identify === 'function') {
        Promise.resolve(window.klaviyo.identify({ email: ctx.email }))
          .then(track)
          .catch(track);
      } else {
        window.klaviyo.push(['identify', { email: ctx.email }]);
        track();
      }
    } else {
      track();
    }
  }

  function trackAddedToFavorites(raw) {
    var base = resolveBase(raw);
    if (!base.productId && !base.productHandle) return;

    enrichProduct(base).then(function (product) {
      if (alreadyTracked(base.productId || base.productHandle, base.variantId)) {
        return;
      }
      var payload = buildPayload(base, product);
      if (!payload['Product ID'] && !payload['Product Name']) return;
      sendToKlaviyo(payload);
    });
  }

  function onWishlistAddClick(event) {
    var path = event.composedPath ? event.composedPath() : [];
    var addBtn = null;

    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (!node || !node.classList) continue;
      if (node.classList.contains('xb-wishlist__add')) {
        addBtn = node;
        break;
      }
    }

    if (!addBtn && event.target && event.target.closest) {
      addBtn = event.target.closest(
        '.xb-wishlist__add, xb-wishlist-button, xb-wishlist-button-auto'
      );
    }
    if (!addBtn) return;

    var isAdd = path.some(function (n) {
      return n && n.classList && n.classList.contains('xb-wishlist__add');
    });
    if (!isAdd && !(addBtn.classList && addBtn.classList.contains('xb-wishlist__add'))) {
      return;
    }

    var data = extractFromPath(path, addBtn);
    if (!data) return;
    rememberPending(data);

    window.setTimeout(function () {
      var key =
        String(data.productId || data.productHandle) +
        ':' +
        String(data.variantId || '');
      if (recentKeys[key]) return;
      trackAddedToFavorites(data);
    }, 900);
  }

  function onWishlistCustomEvent(event) {
    var detail = (event && event.detail) || {};
    var data = {
      productId: detail.productId ? String(detail.productId) : '',
      productTitle: detail.productTitle || detail.title || '',
      productHandle: detail.productHandle || detail.handle || '',
      variantId: detail.variantId ? String(detail.variantId) : '',
      price: detail.price
    };
    rememberPending(data);
    trackAddedToFavorites(data);
  }

  function wrapShopifyAnalytics() {
    if (!window.Shopify || !Shopify.analytics || !Shopify.analytics.publish) {
      return false;
    }
    if (Shopify.analytics.__klaviyoFavoritesWrapped) return true;

    var original = Shopify.analytics.publish.bind(Shopify.analytics);
    Shopify.analytics.publish = function (eventName, payload) {
      try {
        var data = payload && payload.data;
        if (
          eventName === 'analytic_event' &&
          data &&
          data.eventType === 'add_to_wishlist'
        ) {
          var product = data.product || {};
          var productId = product.id || product.productId || '';
          var pending = peekPending(productId) || {};
          trackAddedToFavorites({
            productId: String(productId || pending.productId || ''),
            productHandle: pending.productHandle || '',
            productTitle: pending.productTitle || '',
            variantId: String(product.variantId || pending.variantId || ''),
            price: product.price != null ? product.price : pending.price
          });
        }
      } catch (err) {}
      return original(eventName, payload);
    };

    Shopify.analytics.__klaviyoFavoritesWrapped = true;
    return true;
  }

  function init() {
    ensureKlaviyo();
    // Warm the token cache early
    getStorefrontToken();

    document.addEventListener('click', onWishlistAddClick, true);
    document.addEventListener('xb-wishlist:add', onWishlistCustomEvent);

    if (!wrapShopifyAnalytics()) {
      var tries = 0;
      var timer = window.setInterval(function () {
        tries += 1;
        if (wrapShopifyAnalytics() || tries > 40) {
          window.clearInterval(timer);
        }
      }, 250);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
